import { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CONFIRMATION_MODAL_BODY_PORTAL_ID,
  ModalVariant,
} from 'src/components/ConfirmationModal/confirmation-modal';
import { useConfirmationStore } from 'src/store/confirmation-modal';

export const useConfirmationModal = () => {
  const { isOpen, title, body, openConfirmation, closeConfirmation } = useConfirmationStore();
  const [bodyPortalTarget, setBodyPortalTarget] = useState<HTMLElement | null>(null);

  const confirm = useCallback(
    ({
      body,
      title = 'Are you sure?',
      variant = 'normal',
    }: {
      body: string;
      title?: string;
      variant?: ModalVariant;
    }) => {
      return new Promise<boolean>((res) => {
        openConfirmation(title, body, variant, res);
      });
    },
    [openConfirmation],
  );

  useEffect(() => {
    // The confirmation modal renders in a portal, so the body slot doesn't exist
    // until after the modal opens. We poll with requestAnimationFrame until the
    // portal target is mounted, then store it to enable renderBodyPortal.
    if (!isOpen) {
      setBodyPortalTarget(null);
      return;
    }

    let rafId: number | null = null;
    let attempts = 0;
    const maxAttempts = 120;

    const findPortalTarget = () => {
      const portalTarget = document.getElementById(CONFIRMATION_MODAL_BODY_PORTAL_ID);
      if (portalTarget) {
        setBodyPortalTarget(portalTarget);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        return;
      }

      rafId = requestAnimationFrame(findPortalTarget);
    };

    findPortalTarget();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen]);

  const renderConfirmationBody = useCallback(
    (element: ReactNode) => {
      if (!bodyPortalTarget) {
        return null;
      }

      return createPortal(element, bodyPortalTarget);
    },
    [bodyPortalTarget],
  );

  return {
    isOpen,
    title,
    body,
    confirm,
    closeConfirmation,
    renderConfirmationBody,
  };
};
